///
/// EarthBrowser
///
/// Created by Matt Giger on 9/5/2026
/// Copyright (c) 2026 Geodata Labs
///

import { Database } from "@/earthbrowser/database/Database"
import { type DatasetFeature } from "@/earthbrowser/dataset-distribution/DatasetLayer"

export interface WildfireProperties extends DatasetFeature {
	id: string
	name: string
	acres: number | null
	incident_size: number | null
	percent_contained: number | null
	fire_cause: string | null
	fire_behavior: string | null
	state: string | null
	county: string | null
	short_description: string | null
	personnel: number | null
	discovered: string | null
	contained: string | null
	fire_out: string | null
	date_current: string
	centroid_lat: number
	centroid_lon: number
	bbox_min_lon: number
	bbox_min_lat: number
	bbox_max_lon: number
	bbox_max_lat: number
	/** GeoJSON Polygon or MultiPolygon as a JSON string */
	geometry: string
	ingested_at: string
}

const cBatchSize = 200

export class WildfiresTable {
	public static async create(db: Database): Promise<void> {
		await db.execMany([
			{
				sql: `create table if not exists wildfires (
					id text primary key,
					name text not null,
					acres real,
					incident_size real,
					percent_contained real,
					fire_cause text,
					fire_behavior text,
					state text,
					county text,
					short_description text,
					personnel integer,
					discovered text,
					contained text,
					fire_out text,
					date_current text not null,
					centroid_lat real not null,
					centroid_lon real not null,
					bbox_min_lon real not null,
					bbox_min_lat real not null,
					bbox_max_lon real not null,
					bbox_max_lat real not null,
					geometry text not null,
					ingested_at text not null
				)`
			},
			{ sql: "create index if not exists idx_wildfires_date_current on wildfires(date_current desc)" },
			{ sql: "create index if not exists idx_wildfires_acres on wildfires(acres desc)" },
			{ sql: "create index if not exists idx_wildfires_contained on wildfires(percent_contained)" },
		])
	}

	public static async replaceAll(rows: WildfireProperties[]): Promise<void> {
		await Database.db.exec("delete from wildfires", [], "critical")
		await this.upsert(rows)
	}

	public static async upsert(rows: WildfireProperties[]): Promise<void> {
		const statement = `insert into wildfires (
				id, name, acres, incident_size, percent_contained,
				fire_cause, fire_behavior, state, county, short_description,
				personnel, discovered, contained, fire_out, date_current,
				centroid_lat, centroid_lon,
				bbox_min_lon, bbox_min_lat, bbox_max_lon, bbox_max_lat,
				geometry, ingested_at
			) values (
				?, ?, ?, ?, ?,
				?, ?, ?, ?, ?,
				?, ?, ?, ?, ?,
				?, ?,
				?, ?, ?, ?,
				?, ?
			)
			on conflict(id) do update set
				name = excluded.name,
				acres = excluded.acres,
				incident_size = excluded.incident_size,
				percent_contained = excluded.percent_contained,
				fire_cause = excluded.fire_cause,
				fire_behavior = excluded.fire_behavior,
				state = excluded.state,
				county = excluded.county,
				short_description = excluded.short_description,
				personnel = excluded.personnel,
				discovered = excluded.discovered,
				contained = excluded.contained,
				fire_out = excluded.fire_out,
				date_current = excluded.date_current,
				centroid_lat = excluded.centroid_lat,
				centroid_lon = excluded.centroid_lon,
				bbox_min_lon = excluded.bbox_min_lon,
				bbox_min_lat = excluded.bbox_min_lat,
				bbox_max_lon = excluded.bbox_max_lon,
				bbox_max_lat = excluded.bbox_max_lat,
				geometry = excluded.geometry,
				ingested_at = excluded.ingested_at`

		for (let i = 0; i < rows.length; i += cBatchSize) {
			const batch = rows.slice(i, i + cBatchSize)
			await Database.db.bulkInsert(statement, batch.map(f => [
				f.id,
				f.name,
				f.acres,
				f.incident_size,
				f.percent_contained,
				f.fire_cause,
				f.fire_behavior,
				f.state,
				f.county,
				f.short_description,
				f.personnel,
				f.discovered,
				f.contained,
				f.fire_out,
				f.date_current,
				f.centroid_lat,
				f.centroid_lon,
				f.bbox_min_lon,
				f.bbox_min_lat,
				f.bbox_max_lon,
				f.bbox_max_lat,
				f.geometry,
				f.ingested_at,
			]))
		}
	}

	public static async deleteByIds(ids: string[]): Promise<void> {
		if (ids.length === 0) return
		const placeholders = ids.map(() => "?").join(",")
		await Database.db.exec(`delete from wildfires where id in (${placeholders})`, ids, "critical")
	}

	public static async getAll(): Promise<WildfireProperties[]> {
		const rows = await Database.db.exec(
			`select * from wildfires order by date_current desc`,
			[],
			"low"
		)
		return rows as WildfireProperties[]
	}

	public static async getById(id: string): Promise<WildfireProperties | null> {
		const rows = await Database.db.exec(
			`select * from wildfires where id = ?`,
			[id],
			"low"
		) as WildfireProperties[]
		return rows[0] ?? null
	}

	public static async getCount(): Promise<number> {
		const rows = await Database.db.exec(
			`select count(*) as count from wildfires`,
			[],
			"low"
		) as Array<{ count: number }>
		return rows[0]?.count ?? 0
	}
}

/** A fire counts as active unless it's fully contained or declared out */
export function isFireActive(fire: WildfireProperties): boolean {
	if (fire.fire_out) return false
	return (fire.percent_contained ?? 0) < 100
}

/** Best available size figure: reported incident size, falling back to perimeter acres */
export function fireAcreage(fire: WildfireProperties): number | null {
	return fire.incident_size ?? fire.acres
}
